
import { IStaffPayrollPrintData } from 'features/report/services/types/reportStaff';
import shopSelectors from 'services/shop/selectors';
import { EHideFieldReport, isHideFieldReport } from 'utils/hide-field-report';
import { IUIStaffFieldItem } from '../StaffPayrollDetail';
import { ESalaryPayrollType } from 'features/settingStaff/services/constants';
import { IParamsReportSalaryStaff } from 'features/report/services/types/review';
import { useMemo } from 'react';

const useReceiptCashRoundUpDownTemplate = (params: IParamsReportSalaryStaff) => {
  const allSetting = shopSelectors.data.allSetting();
  const listHideFieldReport = shopSelectors.getListHideFieldReport();
  const staffPayrollFields: IUIStaffFieldItem<IStaffPayrollPrintData>[] = useMemo(() => {
    const cashLabel = allSetting?.labelSalaryCashOrBonus || 'Cash';
    const singleStaff = !!params.staffId;
    return [
      {
        label: 'TOTAL SALES',
        fieldName: 'totalSales',
        component: 'ReportPayrollTitleRow',
      },
      {
        label: 'PAYMENT TYPES',
        fieldName: () => ' ',
        hideValue: true,
        component: 'ReportPayrollCaption2HighLightRow',
        hidden: isHideFieldReport(listHideFieldReport, EHideFieldReport.PAYMENT_TYPE) || !singleStaff,
        fields: [
          {
            label: 'Discounts',
            fieldName: 'totalDiscount',
            component: 'ReportPayrollPaymentRow',
          },
          {
            label: 'Credit Card',
            fieldName: 'creditAmount',
            component: 'ReportPayrollPaymentRow',
          },
          {
            label: 'Cash',
            fieldName: 'cashAmount',
            component: 'ReportPayrollPaymentRow',
          },
          {
            label: 'Gift Card',
            fieldName: 'giftCardAmount',
            component: 'ReportPayrollPaymentRow',
          },
          {
            label: 'Loyalty',
            fieldName: 'loyaltyAmount',
            component: 'ReportPayrollPaymentRow',
            hidden: (data) => !data?.loyaltyAmount,
          },
          {
            label: 'Check',
            fieldName: 'checkAmount',
            component: 'ReportPayrollPaymentRow',
            hidden: (data) => !data?.checkAmount,
          },
          {
            label: 'Other',
            fieldName: 'otherAmount',
            component: 'ReportPayrollPaymentRow',
          },
        ]
      },
      {
        label: (data) => `COMMISSION SPLIT (${data?.commissionPercent}%)`,
        fieldName: 'totalCommission',
        component: 'ReportPayrollCaption2HighLightRow',
        hidden: (data) =>
          isHideFieldReport(listHideFieldReport, EHideFieldReport.COMMISSION)
          || !data?.totalCommission,
      },
      {
        label: 'DISCOUNT',
        fieldName: data => -(data?.staffDiscount || 0),
        component: 'ReportPayrollTitleRow',
        hidden: (data) => !data?.staffDiscount,
      },
      {
        hidden: isHideFieldReport(listHideFieldReport, EHideFieldReport.TIP),
        fields: [
          {
            label: 'COLLECTED TIPS',
            fieldName: 'tipCollected',
            component: 'ReportPayrollTipFeeRow',
            hidden: (data) => !data?.tipCollected,
          },
          {
            label: (data) => `TIP FEE ${(data?.tipFeeType === 'PERCENT' && `(${data?.tipFeeValue || 0}%)`)}`,
            fieldName: data => -(data?.tipFee || 0),
            component: 'ReportPayrollTipFeeRow',
            hidden: (data) => !data?.tipFee,
          },
          {
            label: 'TOTAL TIP',
            fieldName: 'totalTip',
            component: 'ReportPayrollTipFeeRow',
            hidden: (data) => !data?.tipFee,
          },
        ]
      },
      {
        label: 'CLEANING FEE',
        fieldName: data => -(data?.totalFeeCleaning || 0),
        component: 'ReportPayrollTipFeeRow',
        hidden: (data) => !data?.totalFeeCleaning,
      },
      {
        label: 'SUPPLY SHARE',
        fieldName: data => -(data?.supplyShareFee || 0),
        component: 'ReportPayrollTipFeeRow',
      },
      {
        label: 'EMPLOYEE’S COMMISSION',
        fieldName: 'totalSalary',
        component: 'ReportPayrollCaption2HighLightRow',
      },
      {
        label: (data) => `${cashLabel} (${data?.cashPercent || 0}%)`,
        fieldName: 'cashValue',
        component: 'ReportPayrollCaption3Row',
        hidden: (data) => !data?.totalCash,
        className: 'PAYROLL_BONUS',
      },
      {
        label: (data) => `Check (${data?.checkPercent || 0}%)`,
        fieldName: 'checkValue',
        component: 'ReportPayrollCaption3Row',
        className: 'PAYROLL_CHECK',
      },
      {
        label: (data) => `Tip to ${cashLabel} ${data?.tipCashPercent ? ` (${data?.tipCashPercent}%)` : ''}`,
        fieldName: 'tipToCashValue',
        component: 'ReportPayrollCaption3Row',
        hidden: (data) => !data?.tipToCashValue,
        className: 'PAYROLL_TIP_TO_CASH',
      },
      {
        label: (data) => `Tip to Check ${data?.tipCheckPercent ? ` (${data?.tipCheckPercent}%)` : ''}`,
        fieldName: 'tipToCheckValue',
        component: 'ReportPayrollCaption3Row',
        className: 'PAYROLL_TIP_TO_CHECK',
      },

      {
        bt: 'print_line',
        hidden: (data) => data?.payrollType !== ESalaryPayrollType.W2,
        fields: [
          {
            label: (data) => `Federal Tax Withheld (${data?.tax?.federalTaxPercent || 0}%)`,
            fieldName: ['tax', 'federalTax'],
            component: 'ReportPayrollCaption3Row',
            hidden: (data) => !data?.tax?.federalTax,
          },
          {
            label: (data) => `Social Security Tax Withheld (${data?.tax?.socialTaxPercent || 0}%)`,
            fieldName: ['tax', 'socialTax'],
            component: 'ReportPayrollCaption3Row',
            hidden: (data) => !data?.tax?.socialTax,
          },
          {
            label: (data) => `Medicare Tax Withheld (${data?.tax?.medicalTaxPercent || 0}%)`,
            fieldName: ['tax', 'medicalTax'],
            component: 'ReportPayrollCaption3Row',
            hidden: (data) => !data?.tax?.medicalTax,
          },
          {
            label: 'TOTAL PAID OUT',
            fieldName: (data) => (data?.totalCheck || 0) + (data?.totalCash || 0),
            component: 'ReportPayrollCaption2HighLightRow',
          },
          {
            label: `Paid ${cashLabel}`,
            fieldName: 'totalCash',
            component: 'ReportPayrollCaption3Row',
            className: 'TOTAL_CASH',
          },
          {
            label: 'Paid Check',
            fieldName: 'totalCheck',
            component: 'ReportPayrollCaption3Row',
            className: 'TOTAL_CHECK',
          },
        ]
      },

      {
        bt: 'print_line',
        hidden: (data) => data?.payrollType === ESalaryPayrollType.W2,
        fields: [
          {
            label: 'TOTAL PAID OUT',
            fieldName: (data) => (data?.totalCheck || 0) + (data?.totalCash || 0),
            component: 'ReportPayrollCaption2HighLightRow',
          },
          {
            label: `Paid ${cashLabel}`,
            fieldName: 'totalCash',
            component: 'ReportPayrollCaption3Row',
            className: 'TOTAL_CASH',
          },
          {
            label: 'Paid Check',
            fieldName: 'totalCheck',
            component: 'ReportPayrollCaption3Row',
            className: 'TOTAL_CHECK',
          },
        ]
      },

    ] as IUIStaffFieldItem<IStaffPayrollPrintData>[];
  }, [params, params.staffId, allSetting, listHideFieldReport]);

  return staffPayrollFields;
};

export default useReceiptCashRoundUpDownTemplate;